<template>
  <div class="p-4">

    <!-- Sin contrato vigente -->
    <div v-if="noActiveContract" class="alert alert-secondary text-center py-4">
      <i class="ri-calendar-close-line mr-2" style="font-size: 1.5rem;"></i>
      <p class="mb-0 mt-2">Este empleado no tiene contrato vigente.</p>
    </div>

    <div v-else-if="balance">

      <!-- Resumen -->
      <div class="row mb-4">
        <div class="col-md-3">
          <div class="stat-card p-3 border rounded text-center">
            <div class="text-muted small">Empresa</div>
            <div class="font-weight-bold">{{ balance.companyName }}</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="stat-card p-3 border rounded text-center">
            <div class="text-muted small">Régimen laboral</div>
            <div class="font-weight-bold">{{ balance.laborRegime }}</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="stat-card p-3 border rounded text-center">
            <div class="text-muted small">Días pendientes</div>
            <div class="font-weight-bold text-primary h4 mb-0">{{ balance.totalDaysPending }}</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="stat-card p-3 border rounded text-center">
            <div class="text-muted small">Días tomados</div>
            <div class="font-weight-bold h4 mb-0">{{ balance.totalDaysTaken }}</div>
          </div>
        </div>
      </div>

      <!-- Alerta períodos por vencer -->
      <div v-if="balance.hasExpiringPeriods" class="alert alert-warning mb-4">
        <i class="ri-alarm-warning-line mr-2"></i>
        Hay períodos vacaciones próximos a vencer. Se recomienda programar el goce vacacional.
      </div>

      <!-- Botón nueva solicitud -->
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">Períodos vacaciones</h5>
        <router-link
          :to="{ name: 'vacation-requests-create', query: { employeeId } }"
          class="button button-primary button-sm"
        >
          <i class="ri-calendar-check-line mr-1"></i> Nueva solicitud
        </router-link>
      </div>

      <!-- Tabla de períodos -->
      <div class="table-responsive mb-4">
        <table class="custom-table">
          <thead>
            <tr>
              <th>#</th>
              <th>INICIO PERÍODO</th>
              <th>FIN PERÍODO</th>
              <th>VENCE</th>
              <th>GANADOS</th>
              <th>TOMADOS</th>
              <th>PENDIENTES</th>
              <th>ESTADO</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="balance.periods.length === 0 && !balance.truncDays">
              <td colspan="8" class="text-center text-muted py-3">No hay períodos registrados</td>
            </tr>
            <tr v-for="(period, index) in balance.periods" :key="period.id">
              <td>{{ index + 1 }}</td>
              <td>{{ period.startDate }}</td>
              <td>{{ period.endDate }}</td>
              <td>{{ period.expirationDate }}</td>
              <td>{{ period.daysEarned }}</td>
              <td>{{ period.daysTaken }}</td>
              <td><strong>{{ period.daysPending }}</strong></td>
              <td>
                <span :class="['badge', getPeriodStatusColor(period.status)]">{{ getPeriodStatusLabel(period.status) }}</span>
              </td>
            </tr>
            <!-- Período trunco -->
            <tr v-if="balance.truncDays > 0" class="text-muted" style="font-style: italic;">
              <td>—</td>
              <td colspan="2">Período trunco (proporcional)</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>{{ balance.truncDays }}</td>
              <td><span class="badge badge-secondary">TRUNCO</span></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Tabla de solicitudes -->
      <h5 class="mb-3">Solicitudes de vacaciones</h5>
      <div class="table-responsive">
        <table class="custom-table">
          <thead>
            <tr>
              <th>FECHA SOLICITUD</th>
              <th>FECHA INICIO</th>
              <th>FECHA FIN</th>
              <th>DÍAS</th>
              <th>ESTADO</th>
              <th class="text-center">DETALLE</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="requests.length === 0">
              <td colspan="6" class="text-center text-muted py-3">No hay solicitudes registradas</td>
            </tr>
            <tr v-for="req in requests" :key="req.id">
              <td>{{ req.createdAt }}</td>
              <td>{{ req.startDate }}</td>
              <td>{{ req.endDate }}</td>
              <td>{{ req.calendarDays }}</td>
              <td>
                <span :class="['badge', getRequestStatusColor(req.status)]">{{ req.status }}</span>
              </td>
              <td class="text-center">
                <router-link :to="{ name: 'vacation-requests-detail', params: { id: req.id } }" class="text-primary">
                  <i class="ri-eye-line"></i>
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>

    <!-- Loading inicial -->
    <div v-else class="text-center py-4 text-muted">
      <i class="ri-loader-4-line"></i> Cargando...
    </div>

  </div>
</template>

<script>
import { Alert } from 'wize-admin'
import { VacationService } from '@/core/services'
import { Constants } from '@/core/utils'

export default {
  name: 'VacationsTab',
  props: {
    employeeId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      balance: null,
      requests: [],
      noActiveContract: false,
    }
  },
  methods: {
    getPeriodStatusColor(status) {
      const found = Object.values(Constants.VacationPeriodStatus).find(s => s.key === status)
      return found ? found.color : 'badge-gray'
    },
    getPeriodStatusLabel(status) {
      const found = Object.values(Constants.VacationPeriodStatus).find(s => s.key === status)
      return found ? found.label : status
    },
    getRequestStatusColor(status) {
      const found = Object.values(Constants.VacationRequestStatus).find(s => s.key === status)
      return found ? found.color : 'badge-gray'
    },
    async loadBalance() {
      if (!this.employeeId) return
      try {
        const resp = await VacationService.getBalance(this.employeeId)
        const payload = resp.payload
        if (!payload || !payload.contractId) {
          this.noActiveContract = true
        } else {
          this.balance = payload
        }
      } catch (e) {
        this.noActiveContract = true
      }
    },
    async loadRequests() {
      if (!this.employeeId) return
      try {
        const resp = await VacationService.getRequestsByEmployee(this.employeeId)
        this.requests = resp.payload || []
      } catch (e) {
        console.error(e)
      }
    },
  },
  async mounted() {
    if (!this.employeeId) return
    try {
      await Promise.all([this.loadBalance(), this.loadRequests()])
    } catch (e) {
      Alert.error('Error al cargar información de vacaciones')
    }
  },
}
</script>
